import {BlockMath, InlineMath} from "react-katex";
import logGraph from '../../../../../assets/logGraph.png'

const LogisticRegressionCostFunction = () => {
    return (
        <div className="max-w-4xl mx-auto px-4">
            <p className="tw-header">Logistic Regression Cost Function</p>
            <p className="tw-subtitle">The cost function of our logistic regression will have many similarities to that
                of our linear regression model. As explained in the previous section, the point the logistic regression
                model is to classify our input data. In this case it will be binary classification. First we input our
                data into a linear regression model, which will output a value. This value will then be given to our
                sigmoid function which will output a value between 0 and 1, and from there we can apply our custom
                threshold to see what this value will signify.</p>
            <hr className="mt-4 mb-4"/>
            <p className="tw-subtitle">To make this crystal clear I wil be using an example. Let's say we want to
                classify whether a tumor is malignant or benign. Meaning if it is growing/prone to growing, or not.
                We will use the following data: the age of the patient, the GTV, meaning gross tumor volume. This is of
                course
                simplified for the sake of simplicity.
            </p>
            <hr className="mt-4 mb-4"/>
            <p className="tw-subtitle">
                So our data will be represented as:
            </p>
            <BlockMath math="\vec{x} = [60, 5]; y=1"/>
            <p className="tw-subtitle">
                This means we have a patient that is 60 years of age, has a GTV of 5 (in <InlineMath math="cm^3"/>). We
                will now give this data to our
                linear regression model that will then output a value. We know that our data consists of 2 features,
                meaning we will have 2 coefficients, and our bias. Let us crete two arbitrary values for these
                weights and bias. Let us say:
            </p>
            <BlockMath math="\vec{w} = [0.01, 0.2] ; b = -1.5"/>
            <p className="tw-subtitle">
                Now we will apply our linear regression model to our data.
            </p>
            <BlockMath math="z = \vec{w} \cdot \vec{x} + b"/>
            So now we have a logit for the output of this model. Which will be:
            <BlockMath math="z = 0.01 \cdot 60 + 0.2 \cdot 5 + (-1.5) = 0.6 + 1 - 1.5 = 0.1"/>
            <p className="tw-subtitle">
                Now we will apply our sigmoid function to this value.
            </p>
            <BlockMath math="a = \frac{1}{1 + e^{-z}}"/>
            <p className="tw-subtitle">
                Which will equate to:
            </p>
            <BlockMath
                math="a = \frac{1}{1 + e^{-0.1}} = \frac{1}{1 + e^{-0.1}} = \frac{1}{1 + 0.9048} = \frac{1}{1.9048} \approx 0.525"/>
            <p className="tw-subtitle">
                Now let's look at a second example to understand how the cost function works with multiple data points.
            </p>
            <br className="mt-4 mb-4"/>
            <p className="tw-subtitle">
                Second patient data:
            </p>
            <BlockMath math="\vec{x}_2 = [45, 3]; y_2=0"/>
            <p className="tw-subtitle">
                This patient is 45 years old with a GTV of 3 <InlineMath math="cm^3"/>, and the tumor is benign (y=0).
                Using the same weights and bias:
            </p>
            <BlockMath math="z_2 = 0.01 \cdot 45 + 0.2 \cdot 3 + (-1.5) = 0.45 + 0.6 - 1.5 = -0.45"/>
            <BlockMath
                math="a_2 = \frac{1}{1 + e^{-(-0.45)}} = \frac{1}{1 + e^{0.45}} = \frac{1}{1 + 1.568} = \frac{1}{2.568} \approx 0.389"/>
            <br className="mb-4"/>
            <p className="tw-sub-header">Loss Function and Cost Function</p>
            <p className="tw-subtitle">
                For each individual example, we calculate the loss using the binary cross-entropy loss function:
            </p>
            <BlockMath math="L(a, y) = -[y \cdot \log(a) + (1-y) \cdot \log(1-a)]"/>
            <p className="tw-sub-header">Why this function makes sense:</p>
            <p className="tw-subtitle">
                Our value a will be the activation output of our sigmoid function, where value y is the target value. If
                the target value is 1, then you can see that the second term will be cancelled out. When i say second
                term i refer to the following section:
            </p>
            <BlockMath math="(1-y) \cdot \log(1-a)]"/>
            <p className="tw-subtitle">
                As <InlineMath math="1-1 = 0"/> and from there the value of <InlineMath math="\log(1-a)"/> is cancelled
                out. Meaning that the remaining term is :
            </p>
            <BlockMath math="L(a, 1) = -[1 \cdot \log(a)] = -log(a)"/>

            <p className="tw-subtitle">
                And if the taget value were to be 0 then the remaining term would be:
            </p>
            <BlockMath math="L(a, y) = -[(1-y) \cdot \log(1-a)] = -log(1-a)"/>
            <p className="tw-sub-header">If our target is 0:</p>

            <p className="tw-subtitle"> This makes sense as if the purpose of the loss function is to punish the weights
                and bias when their prediction is far from the target. If your target is 0 and you predict a = 0.9, then
                that would equate to </p>
            <BlockMath math="-log(1-0.9) = -log(0.1) = 1"/>
            <p className="tw-subtitle"> And if you were to predict 0 then we should expect 0 loss, and the output would
                look as follows: </p>
            <BlockMath math="-log(1-0) = -log(1) = 0"/>
            <p className="tw-sub-header">If our target is 1:</p>

            <p className="tw-subtitle"> Now if our target value was 1, and we predicted 0.1, it would
                be:</p>
            <BlockMath math="-log(0.1) = 1"/>
            <p className="tw-subtitle"> And if we were to predict our exact target of 1 it would equate to:</p>
            <BlockMath math="-log(1) = 0"/>

            <img src={logGraph}/>
            <p className="tw-subtitle"> The image above simply illustrates how our log and -log function look like. As
                you can see the closer your get to 0 the higher the loss becomes. And keep in mind we only make use of
                -log and you cannot have a negative loss value.</p>

            <br/>
            <p className="tw-subtitle">
                For our first patient (y=1, a≈0.525):
            </p>
            <BlockMath math="L_1 = -[1 \cdot \log(0.525) + 0 \cdot \log(0.475)] = -\log(0.525) \approx 0.644"/>

            <p className="tw-subtitle">
                For our second patient (y=0, a≈0.389):
            </p>
            <BlockMath math="L_2 = -[0 \cdot \log(0.389) + 1 \cdot \log(0.611)] = -\log(0.611) \approx 0.493"/>

            <p className="tw-subtitle">
                The cost function is simply the average of all individual loss functions across all training examples:
            </p>
            <BlockMath math="J(w, b) = \frac{1}{m} \sum_{i=1}^{m} L(a^{(i)}, y^{(i)})"/>

            <p className="tw-subtitle">
                For our two examples:
            </p>
            <BlockMath
                math="J(w, b) = \frac{1}{2} (L_1 + L_2) = \frac{1}{2} (0.644 + 0.493) = \frac{1.137}{2} \approx 0.569"/>

            <p className="tw-subtitle">
                This demonstrates that the cost function is indeed the aggregate average of the loss functions. During
                training,
                we minimize this cost function to find the optimal weights and bias that best classify all our training
                examples.
            </p>



        </div>
    )
}

export default LogisticRegressionCostFunction;